import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './SalonFinder.css';
import { sendCachedRequest } from '../utils/apiCache';
import { BASE_URL } from '../URL/constants';

const DEFAULT_COORDS = { lat: 28.6139, lng: 77.209 };

// Geocode address to get coordinates using OpenStreetMap Nominatim API
const geocodeAddress = async (address, city, state, pinCode) => {
  try {
    const fullAddress = [address, city, state, pinCode].filter(Boolean).join(', ');
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`,
      {
        headers: {
          'User-Agent': 'SalonFinderApp/1.0'
        }
      }
    );
    
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
      };
    }
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

const createPin = (fill) =>
  encodeURIComponent(
    `<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="${fill}" d="M24 4C14.6 4 7 11.7 7 21.1c0 10 11 21.9 15.3 25.9.9.9 2.4.9 3.3 0C30 43 41 31.1 41 21.1 41 11.7 33.4 4 24 4Zm0 24.8c-4.2 0-7.7-3.4-7.7-7.7S19.8 13.4 24 13.4s7.7 3.4 7.7 7.7-3.5 7.7-7.7 7.7Z"/>
    </svg>`
  );

const userIcon = L.icon({
  iconUrl: `data:image/svg+xml,${createPin('#d4145a')}`,
  iconSize: [48, 48],
  iconAnchor: [24, 46],
  popupAnchor: [0, -42],
});

const salonIcon = L.icon({
  iconUrl: `data:image/svg+xml,${createPin('#111827')}`,
  iconSize: [40, 40],
  iconAnchor: [20, 38],
  popupAnchor: [0, -34],
});

const SalonFinder = () => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const userMarkerRef = useRef(null);
  const salonLayerRef = useRef(null);
  const branchMarkersRef = useRef(new Map()); // Store markers by branch id
  const [status, setStatus] = useState('Loading branches...');
  const [branchesWithCoords, setBranchesWithCoords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSalon, setActiveSalon] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  // Fetch branches from API
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoading(true);
        setStatus('Loading branches...');
        
        const response = await sendCachedRequest(
          'POST',
          `${BASE_URL}/v1/public_site/public_site_info/get_list`,
          null,
          { cacheKey: 'public_site_info' }
        );

        const allBranches = response?.data?.data?.company_info?.all_branches || [];
        
        if (allBranches.length > 0) {
          // Format branches
          const formattedBranches = allBranches.map((branch) => ({
            id: branch.id,
            name: branch.branch_name || 'Branch',
            address: branch.address || '',
            city: branch.city || '',
            state: branch.state || '',
            pin_code: branch.pin_code || '',
            phone: branch.phone || '',
          }));
          
          setStatus('Geocoding branch addresses...');
          
          // Geocode all branch addresses
          const geocodedBranches = await Promise.all(
            formattedBranches.map(async (branch) => {
              const coords = await geocodeAddress(
                branch.address,
                branch.city,
                branch.state,
                branch.pin_code
              );
              
              return {
                ...branch,
                coords: coords || DEFAULT_COORDS,
                hasValidCoords: coords !== null,
              };
            })
          );
          
          setBranchesWithCoords(geocodedBranches);
          
          // Log branch info for debugging
          console.log('Geocoded branches:', geocodedBranches);
          const validCount = geocodedBranches.filter(b => b.hasValidCoords).length;
          console.log(`Branches with valid coordinates: ${validCount}/${geocodedBranches.length}`);
          
          // Set first branch as active if available
          if (geocodedBranches.length > 0) {
            setActiveSalon(geocodedBranches[0]);
          }
          
          setStatus('Branches loaded. Allow location access to highlight nearby branches.');
        } else {
          setStatus('No branches found.');
          setBranchesWithCoords([]);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
        setStatus('Unable to load branches. Please try again later.');
        setBranchesWithCoords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBranches();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      zoomControl: false,
    }).setView([DEFAULT_COORDS.lat, DEFAULT_COORDS.lng], 11);
    mapInstance.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    L.control
      .zoom({
        position: 'bottomright',
      })
      .addTo(map);

    salonLayerRef.current = L.layerGroup().addTo(map);

    return () => {
      map.remove();
    };
  }, []);

  // Update map markers when branches with coordinates are loaded
  useEffect(() => {
    if (!salonLayerRef.current || branchesWithCoords.length === 0) return;

    // Clear existing markers
    salonLayerRef.current.clearLayers();
    branchMarkersRef.current.clear(); // Clear the markers map

    // Add markers for each branch
    branchesWithCoords.forEach((branch) => {
      if (branch.hasValidCoords) {
        const fullAddress = [branch.address, branch.city, branch.state, branch.pin_code]
          .filter(Boolean)
          .join(', ');
        
        const marker = L.marker([branch.coords.lat, branch.coords.lng], { icon: salonIcon })
          .addTo(salonLayerRef.current)
          .bindPopup(`<strong>${branch.name}</strong><br/>${fullAddress}`);
        
        // Store marker by branch id for later access
        branchMarkersRef.current.set(branch.id, marker);
      }
    });

    // Fit map to show all branches if we have valid coordinates
    const validBranches = branchesWithCoords.filter((b) => b.hasValidCoords);
    if (validBranches.length > 0) {
      const bounds = L.latLngBounds(
        validBranches.map((b) => [b.coords.lat, b.coords.lng])
      );
      mapInstance.current?.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [branchesWithCoords]);

  const locateUser = useCallback(
    (shouldCenter = true) => {
      if (!mapInstance.current) return;

      if (!navigator.geolocation) {
        setStatus('Location is not supported in this browser.');
        return;
      }

      setStatus('Locating you... Please allow access.');

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setStatus('Showing branches near your current location.');

          if (shouldCenter) {
            mapInstance.current.setView([latitude, longitude], 14, {
              animate: true,
            });
          }

          if (userMarkerRef.current) {
            userMarkerRef.current.setLatLng([latitude, longitude]);
          } else {
            userMarkerRef.current = L.marker([latitude, longitude], {
              icon: userIcon,
              title: 'You are here',
            }).addTo(mapInstance.current);
          }

          userMarkerRef.current.bindPopup('You are here').openPopup();
        },
        () => {
          setStatus('Unable to access your location. Showing default view.');
          mapInstance.current.setView([DEFAULT_COORDS.lat, DEFAULT_COORDS.lng], 11);
        },
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 0,
        }
      );
    },
    []
  );

  // Auto-locate user only after branches are loaded
  useEffect(() => {
    if (branchesWithCoords.length > 0) {
      locateUser(true);
    }
  }, [locateUser, branchesWithCoords.length]);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Calculate distances from user location
  const branchesWithDistance = useMemo(() => {
    return branchesWithCoords.map((branch) => {
      if (!userLocation || !branch.hasValidCoords) {
        return { ...branch, distance: null };
      }
      const distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        branch.coords.lat,
        branch.coords.lng
      );
      return {
        ...branch,
        distance: distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)} km`,
      };
    });
  }, [branchesWithCoords, userLocation, calculateDistance]);

  const focusSalon = useCallback((salon) => {
    setActiveSalon(salon);
    
    if (!mapInstance.current) {
      console.log('Map instance not available');
      return;
    }
    
    if (!salon || !salon.hasValidCoords) {
      console.log('Branch has invalid coordinates or salon is null');
      return;
    }
    
    // Get the marker for this branch
    const marker = branchMarkersRef.current.get(salon.id);
    
    // Focus map on the branch location with smooth animation
    // Use flyTo if available (Leaflet 1.0+), otherwise use setView with panTo
    if (mapInstance.current.flyTo) {
      mapInstance.current.flyTo([salon.coords.lat, salon.coords.lng], 15, {
        animate: true,
        duration: 0.8,
      });
    } else {
      // Fallback for older Leaflet versions
      mapInstance.current.setView([salon.coords.lat, salon.coords.lng], 15, {
        animate: true,
      });
    }
    
    // Open the popup after animation completes
    if (marker) {
      const delay = mapInstance.current.flyTo ? 900 : 500;
      setTimeout(() => {
        marker.openPopup();
      }, delay);
    }
  }, []);

  return (
    <div className="salon-finder-page">
      <section className="finder-hero">
        <p className="finder-label">Salon Finder</p>
        <h1>Discover a Looks Salon near you</h1>
        <p className="finder-subtitle">
          Enable location or pick from our curated list of flagship salons to enjoy luxury grooming
          experiences, bespoke hair rituals, and bridal artistry across India.
        </p>
        <div className="finder-actions">
          <button className="primary" onClick={() => locateUser(true)}>
            Use My Location
          </button>
        </div>
      </section>

      <section className="finder-map-section">
        <div className="finder-status-chip">{status}</div>
        <div ref={mapRef} className="finder-map" role="application" aria-label="Looks Salon map" />
      </section>

      <section className="salon-grid">
        {loading ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
            <p>Loading branches...</p>
          </div>
        ) : branchesWithDistance.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
            <p>No branches found.</p>
          </div>
        ) : (
          branchesWithDistance.map((branch) => {
            const fullAddress = [branch.address, branch.city, branch.state, branch.pin_code]
              .filter(Boolean)
              .join(', ');
            
            return (
              <article
                key={branch.id}
                className={`salon-card ${activeSalon?.id === branch.id ? 'active' : ''}`}
              >
                <div>
                  {branch.distance && (
                    <p className="salon-distance">{branch.distance} away</p>
                  )}
                  <h3>{branch.name}</h3>
                  <p className="salon-address">{fullAddress || 'Address not available'}</p>
                  {branch.city && branch.state && (
                    <p className="salon-services">{branch.city}, {branch.state}</p>
                  )}
                </div>
                <div className="salon-card-actions">
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (branch.hasValidCoords) {
                        console.log('Button clicked for branch:', branch);
                        focusSalon(branch);
                      } else {
                        console.log('Branch has no valid coordinates:', branch);
                        setStatus(`Unable to show ${branch.name} on map - address geocoding failed.`);
                      }
                    }}
                    disabled={!branch.hasValidCoords}
                    className={branch.hasValidCoords ? 'map-button-active' : 'map-button-disabled'}
                    style={{ display: 'block' }}
                  >
                    {branch.hasValidCoords ? 'View on Map' : 'Location Unavailable'}
                  </button>
                  {branch.phone && (
                    <a href={`tel:${branch.phone.replace(/[^0-9]/g, '')}`}>
                      {branch.phone}
                    </a>
                  )}
                </div>
              </article>
            );
          })
        )}
      </section>
    </div>
  );
};

export default SalonFinder;

